import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Group,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Assessment, AssessmentDataPoint, ClinicalNote, TreatmentPlan } from '../clients/data/types';
import type { ParticipantAttendanceFhirStatus } from '@/pages/appointments/utils/participantAttendance';
import type { ProtocolSection, SessionAppointment, SessionClient } from './data/mockSessionData';
import type { SessionAttendanceEntry } from './lib/sessionAttendance';
import type { SessionContactInfo } from './lib/sessionContactInfo';
import type { ClientLocationConfirmation } from '@/lib/sessionLocation/types';
import type { SessionAutosaveStatus } from './hooks/useSessionAutosave';
import type { TreatmentPlanParticipantOption } from './components/TreatmentPlanClientSelect';
import {
  CLINICAL_PANEL_TAB_ITEMS,
  getClinicalPanelVisibleTabs,
  getEffectiveShowPrescribeTab,
} from './lib/clinicalPanelTabs';
import { ClinicalNotesTab } from './tabs/ClinicalNotesTab';
import { TreatmentPlansTab } from './tabs/TreatmentPlansTab';
import { AssessmentsTab } from './tabs/AssessmentsTab';
import { ProtocolsTab } from './tabs/ProtocolsTab';
import { PrescribeTab } from './tabs/PrescribeTab';
import { SessionOverviewTab } from './tabs/SessionOverviewTab';

export { CLINICAL_PANEL_TAB_ITEMS } from './lib/clinicalPanelTabs';

interface ClinicalPanelProps {
  client: SessionClient;
  appointment: SessionAppointment;
  participants: SessionContactInfo[];
  notes: ClinicalNote[];
  plans: TreatmentPlan[];
  plansLoading?: boolean;
  plansError?: string | null;
  assessments: Assessment[];
  assessmentTrends: AssessmentDataPoint[];
  protocols: ProtocolSection[];
  patientId?: string;
  episodeId?: string;
  appointmentId?: string;
  encounterId?: string;
  initialAttendance: Map<string, ParticipantAttendanceFhirStatus>;
  onSaveAttendance: (entries: SessionAttendanceEntry[]) => Promise<void>;
  sidebarNotes: string;
  onSidebarNotesChange: (value: string) => void;
  sidebarNotesStatus?: SessionAutosaveStatus;
  sidebarNotesError?: string | null;
  onSidebarNotesBlur?: () => void;
  onSidebarNotesRetry?: () => void;
  onAssessmentSaved: () => Promise<void>;
  isGroupTherapyEpisode?: boolean;
  groupName?: string;
  groupInitials?: string;
  groupSubtitle?: string;
  showPrescribeTab?: boolean;
  treatmentPlanParticipantOptions?: TreatmentPlanParticipantOption[];
  selectedTreatmentPlanPatientId?: string;
  onSelectedTreatmentPlanPatientChange?: (patientId: string) => void;
  clientLocationConfirmations?: Record<string, ClientLocationConfirmation>;
  clientLocationLoading?: boolean;
  clientLocationSaving?: boolean;
  clientLocationSaveError?: string | null;
  onSaveClientLocation?: (
    patientId: string,
    values: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
      notAtUsualAddress: boolean;
      attestationAccepted: boolean;
    },
  ) => Promise<void>;
  /** overlay = fixed-height Zoom sidebar; modal = shrink-wrap with inner scroll only when needed */
  layout?: 'overlay' | 'modal';
  onClose?: () => void;
  onRegisterFlush?: (flush: (() => Promise<void>) | null) => void;
  includeRecordingTab?: boolean;
  recordingTabContent?: ReactNode;
}

export function ClinicalPanel({
  client,
  appointment,
  participants,
  notes,
  plans,
  plansLoading = false,
  plansError = null,
  assessments,
  assessmentTrends,
  protocols,
  patientId,
  episodeId,
  appointmentId,
  encounterId,
  initialAttendance,
  onSaveAttendance,
  sidebarNotes,
  onSidebarNotesChange,
  sidebarNotesStatus = 'idle',
  sidebarNotesError = null,
  onSidebarNotesBlur,
  onSidebarNotesRetry,
  onAssessmentSaved,
  isGroupTherapyEpisode = false,
  groupName,
  groupInitials,
  groupSubtitle,
  showPrescribeTab = false,
  treatmentPlanParticipantOptions = [],
  selectedTreatmentPlanPatientId,
  onSelectedTreatmentPlanPatientChange,
  clientLocationConfirmations = {},
  clientLocationLoading = false,
  clientLocationSaving = false,
  clientLocationSaveError = null,
  onSaveClientLocation,
  layout = 'overlay',
  onClose,
  onRegisterFlush,
  includeRecordingTab = false,
  recordingTabContent,
}: ClinicalPanelProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<string>(includeRecordingTab ? 'recording' : 'overview');
  const notesFlushRef = useRef<(() => Promise<void>) | null>(null);
  const attendanceFlushRef = useRef<(() => Promise<void>) | null>(null);

  const effectiveShowPrescribeTab = getEffectiveShowPrescribeTab(
    showPrescribeTab,
    isGroupTherapyEpisode,
  );

  const tabItems = useMemo(
    () => getClinicalPanelVisibleTabs(isGroupTherapyEpisode, showPrescribeTab, includeRecordingTab),
    [isGroupTherapyEpisode, showPrescribeTab, includeRecordingTab],
  );

  useEffect(() => {
    const visible = new Set(tabItems.map((tab) => tab.value));
    if (!visible.has(activeTab as (typeof CLINICAL_PANEL_TAB_ITEMS)[number]['value'])) {
      setActiveTab(tabItems[0]?.value ?? 'overview');
    }
  }, [activeTab, tabItems]);

  // When in-person recording becomes available, land on the Recording tab by default.
  const prevIncludeRecordingRef = useRef(includeRecordingTab);
  useEffect(() => {
    if (includeRecordingTab && !prevIncludeRecordingRef.current) {
      setActiveTab('recording');
    }
    prevIncludeRecordingRef.current = includeRecordingTab;
  }, [includeRecordingTab]);

  const isModalLayout = layout === 'modal';
  const healthPlanName = client.insurance.payer?.trim() || client.insurance.plan?.trim() || '—';
  const headerName = isGroupTherapyEpisode ? (groupName ?? 'Group session') : client.name;
  const headerInitials = isGroupTherapyEpisode
    ? (groupInitials ?? 'G')
    : client.initials;
  const headerSubtitle = isGroupTherapyEpisode ? (groupSubtitle ?? undefined) : healthPlanName;

  const selectedTreatmentPlanPatientName = treatmentPlanParticipantOptions.find(
    (option) => option.patientId === selectedTreatmentPlanPatientId,
  )?.name;

  const registerAttendanceFlush = useCallback((flush: (() => Promise<void>) | null) => {
    attendanceFlushRef.current = flush;
  }, []);

  const combinedFlush = useCallback(async () => {
    await Promise.all([
      notesFlushRef.current?.() ?? Promise.resolve(),
      attendanceFlushRef.current?.() ?? Promise.resolve(),
    ]);
  }, []);

  useEffect(() => {
    notesFlushRef.current = onSidebarNotesBlur
      ? async () => {
          onSidebarNotesBlur();
        }
      : null;
  }, [onSidebarNotesBlur]);

  useEffect(() => {
    onRegisterFlush?.(combinedFlush);
    return () => onRegisterFlush?.(null);
  }, [combinedFlush, onRegisterFlush]);

  const handleTabChange = (next: string | null) => {
    if (!next || next === activeTab) return;
    void combinedFlush();
    setActiveTab(next);
  };

  const tabContent = (
    <>
      {activeTab === 'recording' && includeRecordingTab ? recordingTabContent : null}
      {activeTab === 'overview' && (
        <SessionOverviewTab
          participants={participants}
          appointment={appointment}
          initialAttendance={initialAttendance}
          onSaveAttendance={onSaveAttendance}
          onRegisterFlush={registerAttendanceFlush}
          clientLocationConfirmations={clientLocationConfirmations}
          clientLocationLoading={clientLocationLoading}
          clientLocationSaving={clientLocationSaving}
          clientLocationSaveError={clientLocationSaveError}
          onSaveClientLocation={onSaveClientLocation}
          patientId={patientId}
          patientDisplay={client.name}
          episodeId={episodeId}
          appointmentId={appointmentId}
          appointmentStart={appointment.scheduledTime}
        />
      )}
      {activeTab === 'notes' && (
        <ClinicalNotesTab
          notes={notes}
          sessionNotes={sidebarNotes}
          onSessionNotesChange={onSidebarNotesChange}
          sessionNotesStatus={sidebarNotesStatus}
          sessionNotesError={sidebarNotesError}
          onSessionNotesBlur={onSidebarNotesBlur}
          onSessionNotesRetry={onSidebarNotesRetry}
          hidePastClinicalNotes={isGroupTherapyEpisode}
          appointmentId={appointmentId}
          patientId={patientId}
          encounterId={encounterId}
        />
      )}
      {activeTab === 'tx-plans' && (
        <TreatmentPlansTab
          plans={plans}
          loading={plansLoading}
          error={plansError}
          groupMode={isGroupTherapyEpisode}
          participantOptions={treatmentPlanParticipantOptions}
          selectedPatientId={selectedTreatmentPlanPatientId}
          onSelectedPatientChange={onSelectedTreatmentPlanPatientChange}
          selectedPatientName={selectedTreatmentPlanPatientName}
        />
      )}
      {activeTab === 'assessments' && !isGroupTherapyEpisode && (
        <AssessmentsTab
          assessments={assessments}
          trends={assessmentTrends}
          patientId={patientId}
          episodeId={episodeId}
          appointmentId={appointmentId}
          encounterId={encounterId}
          onSaved={onAssessmentSaved}
        />
      )}
      {activeTab === 'help' && <ProtocolsTab protocols={protocols} />}
      {activeTab === 'prescribe' && effectiveShowPrescribeTab && <PrescribeTab />}
    </>
  );

  return (
    <Box
      h={isModalLayout ? undefined : '100%'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: 'var(--mantine-radius-sm)',
        overflow: 'hidden',
      }}
    >
      <Box px="md" py="sm">
        <Group gap="sm" wrap="nowrap" align="center">
          <Avatar size={32} radius="xl" color="gold" variant="filled" style={{ flexShrink: 0 }}>
            <Text size="xs" fw={700} c="white">{headerInitials}</Text>
          </Avatar>
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} truncate>{headerName}</Text>
            {headerSubtitle ? (
              <Text size="xs" c="dimmed" truncate>{headerSubtitle}</Text>
            ) : null}
          </Stack>
          <Badge size="xs" variant="light" color="green" style={{ flexShrink: 0 }}>
            In Session
          </Badge>
          {onClose ? (
            <Tooltip label="Close clinical sidebar">
              <ActionIcon
                variant="subtle"
                color="gray"
                size={44}
                radius="md"
                aria-label="Close clinical sidebar"
                onClick={onClose}
                style={{ flexShrink: 0 }}
              >
                <IconX size={20} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List
          grow
          px={4}
          style={{
            borderBottom: '1px solid var(--mantine-color-gray-2)',
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }}
        >
          {tabItems.map((tab) => {
            const active = activeTab === tab.value;
            return (
              <Tooltip key={tab.value} label={tab.label} position="bottom" withArrow>
                <Tabs.Tab
                  value={tab.value}
                  px={2}
                  py={6}
                  aria-label={tab.label}
                  style={{
                    flex: '1 1 0',
                    minWidth: 0,
                    height: 'auto',
                    color: active ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-dimmed)',
                  }}
                >
                  <Stack gap={2} align="center" justify="center" style={{ width: '100%' }}>
                    <tab.icon size={16} stroke={1.5} />
                    <Text
                      fz={10}
                      fw={active ? 600 : 500}
                      ta="center"
                      style={{
                        lineHeight: 1.15,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tab.label}
                    </Text>
                  </Stack>
                </Tabs.Tab>
              </Tooltip>
            );
          })}
        </Tabs.List>
      </Tabs>

      {isModalLayout ? (
        <ScrollArea.Autosize mah="calc(85vh - 220px)" px="md" pt="sm" pb="md" offsetScrollbars type="auto">
          {tabContent}
        </ScrollArea.Autosize>
      ) : (
        <ScrollArea flex={1} px="md" pt="sm" pb="md" offsetScrollbars>
          {tabContent}
        </ScrollArea>
      )}
    </Box>
  );
}
